import { useCallback, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { fleetInvalidationKeys, type FleetMessage, type QueryKeyPrefix } from "./fleet-stream";
import { useFleetStream } from "./use-fleet-stream";

const DEFAULT_INVALIDATION_WINDOW_MS = 250;

function keyId(queryKey: QueryKeyPrefix): string {
  return JSON.stringify(queryKey);
}

export function createFleetInvalidationScheduler(
  invalidate: (queryKey: QueryKeyPrefix) => void,
  waitMs = DEFAULT_INVALIDATION_WINDOW_MS,
): { enqueue: (message: FleetMessage) => void; dispose: () => void } {
  const pending = new Map<string, QueryKeyPrefix>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const flush = () => {
    timer = undefined;
    const keys = [...pending.values()];
    pending.clear();
    for (const queryKey of keys) invalidate(queryKey);
  };

  return {
    enqueue(message) {
      for (const queryKey of fleetInvalidationKeys(message)) pending.set(keyId(queryKey), queryKey);
      if (pending.size === 0 || timer) return;
      timer = setTimeout(flush, waitMs);
    },
    dispose() {
      if (timer) clearTimeout(timer);
      timer = undefined;
      pending.clear();
    },
  };
}

/**
 * FleetHub の push を TanStack Query の stale 化に変換する。
 * 月次集計や通知一覧は FleetHub の責務外なので、該当 query の polling はフォールバックとして残す。
 */
export function useFleetRealtimeInvalidation(opts: {
  apiBaseUrl: string;
  enabled?: boolean;
}): void {
  const queryClient = useQueryClient();
  const queryClientRef = useRef(queryClient);
  queryClientRef.current = queryClient;
  const schedulerRef = useRef<ReturnType<typeof createFleetInvalidationScheduler> | null>(null);
  if (!schedulerRef.current) {
    schedulerRef.current = createFleetInvalidationScheduler((queryKey) => {
      void queryClientRef.current.invalidateQueries({ queryKey });
    });
  }
  useEffect(() => () => schedulerRef.current?.dispose(), []);
  const onMessage = useCallback((message: FleetMessage) => {
    schedulerRef.current?.enqueue(message);
  }, []);

  useFleetStream({ ...opts, onMessage });
}
